/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "Hồ sơ & định danh" — one compact dossier panel (owner 2026-10-01, modelled on the HostBill
 * screen the owner uses): upload a file (with the declaration template, eKYC and the masked
 * identity details one click away), a short checklist, the uploaded files with their status and
 * the actions on them (approve / refuse, ask for more, the registrar's SmartCA signature,
 * replace), and the dossier as VNNIC holds it — or, before it is sent, what is still missing.
 * The version history opens in a drawer. Statuses and reasons: `lib/dossierPanel.ts`.
 */

import { Check, CircleAlert, Clock, Download, ExternalLink, FileText, FileUp, History, IdCard, MessageSquarePlus, PenLine, RefreshCw, ScanFace, ShieldCheck, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { api, isApiError, type DomainDetail, type DomainDossier } from '../../../api';
import type { DomainDocument, DomainHstmDossier, DomainVnnic } from '../../../api/areas/core';
import type { SignatureQueueItem } from '../../../api/areas/postOperations';
import type { DossierDocumentType } from '../../../api/areas/dossierQueue';
import { canApprove, type WorkItem } from '../../../api/areas/work';
import type { HstmBoundFile } from '../../../api/types';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Field } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EmptyState, Loading } from '../../../components/States';
import { SigningError } from '../../../components/SigningError';
import { SigningServiceNotice } from '../../../components/SigningServiceNotice';
import { useWrite, WriteError } from '../../../components/Write';
import { holderRegistrationForms, declarationChecks } from '../../../lib/dossierChecklist';
import { dossierChecklist, fileStatus, vnnicBlockers, vnnicState, type Blocker, type CheckState, type FileReason, type FileState, type VnnicState } from '../../../lib/dossierPanel';
import { DOCUMENT_CODE, dossierFileName } from '../../../lib/dossierFileNames';
import { addressLine } from '../../../lib/address';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSigningService } from '../../../lib/signingService';
import { SystemReason } from '@/components/SystemReason';
import { useApi } from '../../../lib/useApi';
import { ActionDialog, FormActions } from '../../compliance/ui';
import { FileViewer, ReadFileDialog, saveBlob, type FileTarget } from '../../declarations/shared';
import { DecisionForm } from '../../work/WorkItemPage';
import { RemindForm } from '../../work/DossierReviewSheet';
import { useVocab } from '../refs';
import { DOSSIER, StatusBadge } from '../status';
import type { DomainTabProps } from './shared';
import { uploadExtensions, uploadProblem } from '@/lib/uploadRule';
import { useAccess } from '@/lib/access';
import { DeclarationReviewPanel } from '../../declarations/DeclarationReviewPanel';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const STATE_TONE: Record<FileState, Tone> = { APPROVED: 'ok', PENDING: 'pending', REJECTED: 'serious' };
const VNNIC_TONE: Record<VnnicState, Tone> = { APPROVED: 'ok', PENDING: 'pending', REJECTED: 'serious', NOT_SENT: 'neutral' };
const DECLARATION_PURPOSES = new Set(['REGISTRATION_FORM', 'DOMAIN_DECLARATION', 'TRANSFEREE_DECLARATION']);

function formatSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  return bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

const readBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.readAsDataURL(file);
  });

/** A disabled control keeps its reason on hover (a disabled button gets no pointer events). */
function Why({ why, children }: { why: string | null; children: ReactNode }) {
  return why ? (
    <span title={why} className="inline-flex">
      {children}
    </span>
  ) : (
    <>{children}</>
  );
}

/** Label: value, two columns, dense (like the registry's own screen). */
function Dense({ rows }: { rows: (readonly [ReactNode, ReactNode] | null | false)[] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2">
      {rows.filter((r): r is readonly [ReactNode, ReactNode] => Boolean(r)).map(([k, v], i) => (
        <div key={i} className="grid min-w-0 grid-cols-[9.5rem_minmax(0,1fr)] gap-2">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="min-w-0 break-words">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

function Block({ id, title, actions, children, flush }: { id: string; title: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section id={id} className="scroll-mt-20 border-t py-3.5 first:border-t-0" data-dossier-block={id}>
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2 px-5">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {actions ? <div className="flex flex-wrap items-center gap-1.5">{actions}</div> : null}
      </div>
      <div className={cn(!flush && 'px-5')}>{children}</div>
    </section>
  );
}

/** One file of the dossier, as the table shows it. */
interface Row {
  id: string;
  purpose: string;
  fileType: number | null;
  doc: DomainDocument | null;
  bound: HstmBoundFile | null;
  mime: string | null;
  size: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  sent: boolean;
  state: FileState;
  reason: string;
  reasonCode: FileReason;
  declaration: boolean;
}

/** Everything the panel reads, loaded once. */
function useDossierData(detail: DomainDetail, x: DomainDossier) {
  const vn = detail.domain.tld_class.startsWith('VN') || detail.domain.tld.endsWith('vn');
  const docs = useApi((signal) => api.listDomainDocuments(detail.domain.id, { signal }), [detail.domain.id, detail.domain.version]);
  const vnnic = useApi((signal) => (vn ? api.getDomainVnnic(detail.domain.id, { signal }) : Promise.resolve<DomainVnnic>({ sync: null })), [detail.domain.id, vn]);
  const declarations = useApi((signal) => api.listDomainDeclarations(detail.domain.ascii_name, { signal }), [detail.domain.ascii_name, detail.domain.version]);
  const queue = useApi((signal) => api.getDossierQueueItem(x.id, { signal }), [x.id, detail.domain.version]);
  const reviewId = queue.status === 'success' ? (queue.data?.review_item_id ?? null) : null;
  const review = useApi<WorkItem | null>((signal) => (reviewId ? api.getWorkItem(reviewId, { signal }) : Promise.resolve(null)), [reviewId]);
  const signing = useApi(
    (signal) => (vn ? api.listSignatureQueue({ limit: 200 }, { signal }).then((r) => r.items.filter((i) => i.dossier_id === x.id), () => [] as SignatureQueueItem[]) : Promise.resolve([] as SignatureQueueItem[])),
    [x.id, vn, detail.domain.version],
  );
  const batches = useApi((signal) => (vn ? api.listSignatureBatches({ signal }).then((b) => b.settings.daily_at, () => '16:00') : Promise.resolve('16:00')), [vn]);
  return { vn, docs, vnnic, declarations, queue, review, signing, batchTime: batches.status === 'success' ? batches.data : '16:00' };
}

export function DossierIdentityTab({ detail, reload }: DomainTabProps) {
  const { t: td } = useTranslation('domains');
  const x = detail.dossier;
  if (!x) {
    return (
      <EmptyState title={td('dossier.none')} icon={FileText}>
        {td('dossier.noneBody')} <StatusBadge map={DOSSIER} value={detail.domain.dossier_state} />
      </EmptyState>
    );
  }
  return <DossierPanel detail={detail} x={x} reload={reload} />;
}

type Dialog =
  | { kind: 'upload'; type: DossierDocumentType; file: File }
  | { kind: 'template' }
  | { kind: 'ekyc' }
  | { kind: 'identity' }
  | { kind: 'approve' | 'reject' }
  | { kind: 'remind' }
  | { kind: 'sign'; row: Row }
  | null;

function DossierPanel({ detail, x, reload }: { detail: DomainDetail; x: DomainDossier; reload?: () => void }) {
  const { t, i18n } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const { t: tw } = useTranslation('work');
  const { t: tq } = useTranslation('dossiers');
  const tr = t as unknown as Lookup;
  const trd = td as unknown as Lookup;
  const vocab = useVocab();
  const data = useDossierData(detail, x);
  const access = useAccess();
  const mayUpload = !access.client && access.has(['work_item.manage', 'dossier.review']);
  const mayReview = !access.client && access.has(['dossier.review']);
  const review = data.review.status === 'success' && data.review.data?.status === 'OPEN' ? data.review.data : null;
  const queued = data.queue.status === 'success' ? data.queue.data : null;
  const ownForms = data.declarations.status === 'success' ? holderRegistrationForms(data.declarations.data.declarations, { currentSubjectId: detail.domain.current_subject_id, pinnedVersionId: x.subject_version_id }) : [];
  const [dialog, setDialog] = useState<Dialog>(null);
  const [history, setHistory] = useState(false);
  // "Xem" / "Tải về" a file of the dossier (the platform's own copy, with a reason).
  const [viewing, setViewing] = useState<(FileTarget & { id: string }) | null>(null);
  const files = x.vnnic_files ?? null;
  const individual = detail.domain.subject_type === 'INDIVIDUAL';
  const refresh = () => {
    data.docs.reload();
    data.vnnic.reload();
    data.queue.reload();
    data.review.reload();
    data.signing.reload();
    data.declarations.reload();
    reload?.();
  };

  const fileTypeName = (n: number) => tr(`dossierPanel.fileType.${n}`, { defaultValue: trd(`dossier.fileTypes.${n}`, { defaultValue: `#${n}` }) });
  const certify = files?.certify_method ?? x.hstm_certify_method ?? null;
  const certifyName = (n: number | null, fallback?: string | null) => (n === null ? '—' : t('dossierPanel.vnnic.methodValue', { n, name: trd(`dossier.certifyMethods.${n}`, { defaultValue: fallback ?? '' }) }));
  const operation = files?.document_type_name ?? vocab('dossierType', x.dossier_type);

  // --- rows -------------------------------------------------------------------------------------
  const sync = data.vnnic.status === 'success' ? data.vnnic.data.sync : null;
  const hstm: DomainHstmDossier | null = sync?.hstm ?? null;
  const state: VnnicState | null = !data.vn ? null : data.vnnic.status === 'success' ? vnnicState(hstm?.status, sync?.status) : null;
  const plannedType = new Map((files?.files ?? []).map((f) => [f.document_version_id, f.file_type]));
  const rejected = new Map((files?.rejected ?? []).map((r) => [r.document_version_id, r]));
  const needsRegistrar = new Set((files?.required ?? []).filter((r) => r.signed_status === 2).flatMap((r) => (r.file_type !== null ? [r.file_type] : (r.any_of ?? []).flat())));
  const docs = data.docs.status === 'success' ? data.docs.data.items.filter((d) => d.is_current_version !== false) : [];
  const bound = (files?.bound_files ?? []).filter((b) => !b.superseded);
  const ids = [...new Set([...docs.map((d) => d.document_version_id), ...bound.map((b) => b.document_version_id)])];
  const replacedIds = new Set(x.documents.filter((d) => d.superseded_by_registrar_signature).map((d) => d.document_version_id));
  const computed: Row[] = ids
    .filter((id) => !replacedIds.has(id))
    .map((id) => {
      const doc = docs.find((d) => d.document_version_id === id) ?? null;
      const b = bound.find((v) => v.document_version_id === id) ?? null;
      const purpose = doc?.purpose ?? b?.purpose ?? 'OTHER';
      const fileType = plannedType.get(id) ?? rejected.get(id)?.file_type ?? (DECLARATION_PURPOSES.has(purpose) ? 5 : null);
      const status = fileStatus({
        fileType,
        bound: b ?? (x.documents.find((d) => d.document_version_id === id)?.registrar_signed ? { signed_by_registrar: true, signed_by_subject: false } : null),
        check: doc?.verification_status ?? null,
        rejectedProblem: rejected.get(id)?.problem ?? null,
        dossierComplete: x.status === 'COMPLETE',
        certifyMethod: certify,
        needsRegistrarSignature: fileType !== null && needsRegistrar.has(fileType),
        pendingReason: ownForms.some((f) => f.document_version_id === id) ? 'GENERATED' : review ? undefined : individual && x.verification_status !== 'VERIFIED' ? 'AWAITING_IDENTITY' : doc?.verification_status === 'PENDING' ? 'AWAITING_CHECK' : 'STORED',
      });
      return {
        id,
        purpose,
        fileType,
        doc,
        bound: b,
        mime: doc?.mime_type ?? b?.mime_type ?? null,
        size: b?.size_bytes ?? doc?.size_bytes ?? null,
        createdAt: doc?.uploaded_at ?? x.documents.find((d) => d.document_version_id === id)?.bound_at ?? null,
        updatedAt: doc?.bound_at ?? null,
        sent: state !== null && state !== 'NOT_SENT' && plannedType.has(id),
        state: status.state,
        reason: tr(`dossierPanel.reason.${status.reason}`),
        reasonCode: status.reason,
        declaration: fileType === 5,
      };
    })
    .sort((a, b) => (a.fileType === 5 ? -1 : b.fileType === 5 ? 1 : (a.fileType ?? 99) - (b.fileType ?? 99)));
  // The domain declaration (#14) the platform generates from the subject is approved once the
  // organisation's signed information (#27) is (owner 2026-10-07); the registrar's signature stays
  // its own step (the signing batch, "NĐK đã ký bản khai").
  const orgSigned = computed.some((r) => r.purpose === 'SIGNED_ORGANIZATION_INFO' && r.state === 'APPROVED');
  const allRows: Row[] = computed.map((r) =>
    orgSigned && r.declaration && r.reasonCode === 'GENERATED'
      ? { ...r, state: 'APPROVED', reasonCode: 'ORG_DECLARATION', reason: tr('dossierPanel.reason.ORG_DECLARATION') }
      : r,
  );
  // One file per kind in a domain's dossier (owner 2026-10-07): the newest is the dossier's, the
  // older ones are history only. Unclassified attachments (no VNNIC file type, purpose OTHER) are
  // each their own.
  const kindOf = (r: Row) => (r.fileType !== null ? `t${r.fileType}` : r.purpose !== 'OTHER' ? `p${r.purpose}` : r.id);
  const newest = new Map<string, Row>();
  for (const r of allRows) {
    const n = newest.get(kindOf(r));
    const [a, b] = [r.createdAt ?? '', n?.createdAt ?? ''];
    if (!n || a > b || (a === b && r.id > n.id)) newest.set(kindOf(r), r);
  }
  const rows = allRows.filter((r) => newest.get(kindOf(r)) === r);
  const olderRows = allRows.filter((r) => newest.get(kindOf(r)) !== r);

  // --- checklist --------------------------------------------------------------------------------
  const decl = declarationChecks(data.declarations.status === 'success' ? data.declarations.data.declarations : null, { currentSubjectId: detail.domain.current_subject_id, pinnedVersionId: x.subject_version_id });
  const hasDeclFile = rows.some((r) => r.declaration);
  const registrarSigned = rows.some((r) => r.declaration && r.bound?.signed_by_registrar) || x.documents.some((d) => d.registrar_signed) || (x.registrar_attestations ?? []).some((a) => a.status === 'VALID');
  const checklist = dossierChecklist({
    declared: decl.declared === null && !hasDeclFile ? null : Boolean(decl.declared || hasDeclFile),
    accepted: decl.signed === null && !registrarSigned ? (x.status === 'COMPLETE' ? true : null) : Boolean(decl.signed || registrarSigned || x.status === 'COMPLETE'),
    verified: x.verification_status === 'VERIFIED',
    rightSubject: x.subject_id === detail.domain.current_subject_id,
    files,
    registrarSigned,
    vnnic: state,
    vn: data.vn,
  });

  // --- the review behind approve / refuse / ask for more ----------------------------------------
  const template = ownForms[0] ?? null;
  const signer = ownForms.flatMap((d) => d.uploads.flatMap((u) => u.signature?.signer_hints ?? []))[0] ?? null;

  // --- upload toolbar ---------------------------------------------------------------------------
  // "Loại hồ sơ": the catalogue's types staff may upload to this dossier (its TLD and operation).
  const uploadTypes = useApi((signal) => api.listDossierUploadTypes(x.id, { signal }), [x.id]);
  const slots = uploadTypes.status === 'success' ? uploadTypes.data.items : [];
  // "Bắt buộc": the catalogue's types required in this dossier (its TLD, operation and registrant).
  const requiredTypes = uploadTypes.status === 'success' ? (uploadTypes.data.required ?? []) : [];
  const requiredIds = new Set(requiredTypes.map((r) => r.id));
  const typeName = (d: { code: number; name_vi: string; name_en: string }) => `#${d.code} ${i18n.language.startsWith('en') ? d.name_en : d.name_vi}`;
  const [picked, setPurpose] = useState('');
  const purpose = slots.some((d) => d.id === picked) ? picked : (slots[0]?.id ?? '');
  const pickedType = slots.find((d) => d.id === purpose) ?? null;
  const fileInput = useRef<HTMLInputElement>(null);
  const ekyc = x.verification_copy?.evidence.find((e) => e.evidence_type === 'EKYC_RESULT') ?? null;
  const ekycWhy = !individual ? t('dossierPanel.ekyc.org') : null;

  const typeOf = (p: string) => slots.find((d) => d.upload_purpose === p || d.purposes.includes(p)) ?? null;
  const onReplace = (r: Row) => {
    setPurpose(typeOf(r.purpose)?.id ?? '');
    fileInput.current?.click();
  };

  const uploadWhy = !slots.length ? t('dossierPanel.upload.noSlots') : null;
  const pendingIdentity = x.verification_status !== 'VERIFIED' && !['COMPLETE', 'SUPERSEDED', 'EXPIRED', 'INVALID'].includes(x.status);
  const checking = ownForms.some((f) => f.uploads.some((u) => u.status === 'PENDING'));
  const reviewTitle = review?.kind === 'DECLARATION_REVIEW' ? t('dossierPanel.next.approvePaper') : t('dossierPanel.next.approve');
  const reviewId = review?.id;
  const reloadQueue = data.queue.reload;
  const reloadDeclarations = data.declarations.reload;
  const reloadDocs = data.docs.reload;
  useEffect(() => {
    if (!checking || reviewId) return;
    const timer = window.setInterval(() => {
      reloadQueue();
      reloadDeclarations();
      reloadDocs();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [checking, reviewId, reloadQueue, reloadDeclarations, reloadDocs]);

  return (
    <div className="rounded-xl border bg-card" data-dossier-panel>
      {/* Header: what the dossier is, in one line. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-5 py-3">
        <h2 className="text-[15px] font-semibold">{t('dossierPanel.title')}</h2>
        <StatusBadge map={DOSSIER} value={x.status} />
        <span className="text-xs text-muted-foreground">
          {[operation, t('dossierPanel.version', { n: x.dossier_version }), certify !== null ? certifyName(certify, files?.certify_method_name) : null, x.subject_display_name ?? detail.domain.subject_display_name].filter(Boolean).join(' · ')}
        </span>
        <span className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setHistory(true)}>
            <History /> {t('dossierPanel.history')}
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to={`/dossiers/${encodeURIComponent(x.id)}`}>
              <ExternalLink /> {t('dossierPanel.open')}
            </Link>
          </Button>
        </span>
      </div>

      <Block id="dossier-next" title={t('dossierPanel.next.title')} actions={review && mayReview ? (
        <>
          {canApprove(review) ? <Button size="sm" onClick={() => setDialog({ kind: 'approve' })}><Check /> {reviewTitle}</Button> : null}
          <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'reject' })}><X /> {t('dossierPanel.files.reject')}</Button>
        </>
      ) : pendingIdentity && queued && mayUpload ? <Button size="sm" variant="outline" onClick={() => setDialog({ kind: 'remind' })}><MessageSquarePlus /> {individual ? t('dossierPanel.next.remindEkyc') : tq('action.remind')}</Button> : null}>
        {data.queue.status === 'error' ? <DataError error={data.queue.error} onRetry={data.queue.reload} /> : data.review.status === 'error' ? <DataError error={data.review.error} onRetry={data.review.reload} /> : review ? (
          <p className="text-[13px] text-muted-foreground">{t('dossierPanel.next.review')}</p>
        ) : pendingIdentity ? (
          <p className="text-[13px] text-muted-foreground">{individual ? t('dossierPanel.next.individual') : checking ? t('dossierPanel.next.checking') : t('dossierPanel.next.paper')}</p>
        ) : <p className="text-[13px] text-muted-foreground">{x.status === 'COMPLETE' ? t('dossierPanel.next.complete') : t('dossierPanel.next.sign')}</p>}
        {review?.kind === 'DECLARATION_REVIEW' ? <DeclarationReviewPanel key={review.id} item={review} /> : null}
      </Block>

      {/* 1. Upload toolbar. */}
      <Block id="dossier-upload" title={t('dossierPanel.upload.title')}>
        {uploadTypes.status === 'error' ? <DataError error={uploadTypes.error} onRetry={uploadTypes.reload} /> : null}
        {mayUpload ? <>
        <div className="flex flex-wrap items-center gap-2">
          <label className="sr-only" htmlFor="dossier-kind">
            {t('dossierPanel.upload.kind')}
          </label>
          <select id="dossier-kind" className="h-8 max-w-56 rounded-md border bg-background px-2 text-sm" value={purpose} onChange={(e) => setPurpose(e.target.value)} disabled={!slots.length} aria-label={t('dossierPanel.upload.kind')}>
            {!slots.length ? <option value="">{t('dossierPanel.upload.kind')}</option> : null}
            {slots.map((d) => (
              <option key={d.id} value={d.id} data-required={requiredIds.has(d.id) ? '' : undefined}>
                {requiredIds.has(d.id) ? t('dossierPanel.upload.requiredOption', { name: typeName(d) }) : typeName(d)}
              </option>
            ))}
          </select>
          <input
            ref={fileInput}
            type="file"
            tabIndex={-1}
            accept={(pickedType ? uploadExtensions(pickedType) : ['pdf', 'png', 'jpg', 'jpeg']).map((e) => `.${e}`).join(',')}
            className="sr-only"
            aria-label={t('dossierPanel.upload.file')}
            onChange={(e) => {
              const selected = e.target.files?.[0];
              if (selected && pickedType) setDialog({ kind: 'upload', type: pickedType, file: selected });
              e.target.value = '';
            }}
          />
          <Why why={uploadWhy}>
            <Button size="sm" disabled={uploadWhy !== null} onClick={() => fileInput.current?.click()}>
              <Upload /> {t('dossierPanel.upload.manual')}
            </Button>
          </Why>
          <Why why={template ? null : t('dossierPanel.upload.noTemplate')}>
            <Button size="sm" variant="outline" disabled={!template} title={t('dossierPanel.upload.templateHint')} onClick={() => setDialog({ kind: 'template' })}>
              <Download /> {t('dossierPanel.upload.template')}
            </Button>
          </Why>
          {ekyc ? <Why why={ekycWhy}>
            <Button size="sm" variant="outline" disabled={ekycWhy !== null} onClick={() => setDialog({ kind: 'ekyc' })}>
              <ScanFace /> {t('dossierPanel.ekyc.title')}
            </Button>
          </Why> : null}
          <Button size="sm" variant="outline" disabled={!x.subject_id} onClick={() => setDialog({ kind: 'identity' })}>
            <IdCard /> {individual ? t('dossierPanel.upload.identity') : t('dossierPanel.upload.identityOrg')}
          </Button>
        </div>
        </> : null}
      </Block>

      {/* Short checklist. */}
      <Block id="dossier-checklist" title={t('dossierPanel.checklist.title')}>
        <ul className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2" data-dossier-checklist>
          {checklist.map((c) => (
            <li key={c.id} className="flex items-center gap-2 text-[13px]" data-check={c.state}>
              <CheckIcon state={c.state} label={tr(`dossierPanel.checklist.state.${c.state}`)} />
              <a href={`#${c.id === 'vnnic' ? 'dossier-vnnic' : c.id === 'verified' ? 'dossier-upload' : 'documents'}`} className={cn('hover:underline', c.state !== 'ok' && 'text-muted-foreground')}>
                {c.id === 'vnnic' ? (c.vnnic ? tr(`dossierPanel.checklist.vnnic.${c.vnnic}`) : t('dossierPanel.checklist.vnnicUnknown')) : tr(`dossierPanel.checklist.${c.id}`)}
              </a>
            </li>
          ))}
          {requiredTypes.map((r) => (
            <li key={`required-${r.id}`} className="flex items-center gap-2 text-[13px]" data-check={r.satisfied_by ? 'ok' : 'missing'} data-required-type={r.id}>
              <CheckIcon state={r.satisfied_by ? 'ok' : 'missing'} label={tr(`dossierPanel.checklist.state.${r.satisfied_by ? 'ok' : 'missing'}`)} />
              <a href="#documents" className={cn('hover:underline', !r.satisfied_by && 'text-muted-foreground')}>
                {r.satisfied_by === 'EKYC' ? t('dossierPanel.checklist.requiredEkyc', { name: typeName(r) }) : t('dossierPanel.checklist.required', { name: typeName(r) })}
              </a>
            </li>
          ))}
        </ul>
      </Block>

      {/* 2. Uploaded files. */}
      <Block
        id="documents"
        title={t('dossierPanel.files.title')}
        flush
      >
        {data.docs.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={2} />
          </div>
        ) : data.docs.status === 'error' ? (
          <div className="px-5">
            <DataError error={data.docs.error} onRetry={data.docs.reload} title={t('dossierPanel.files.failed')} />
          </div>
        ) : (
          <DataTable<Row>
            className="border-y"
            columns={[
              {
                key: 'kind',
                header: t('dossierPanel.files.kind'),
                className: 'min-w-28 whitespace-normal px-2.5 first:pl-5',
                cell: (r) => (
                  <span className="grid leading-tight">
                    <span className="text-[13px] font-medium">{r.fileType !== null ? fileTypeName(r.fileType) : vocab('purpose', r.purpose)}</span>
                    <span className="text-xs text-muted-foreground">{operation}</span>
                  </span>
                ),
              },
              {
                key: 'status',
                header: t('dossierPanel.files.status'),
                className: 'min-w-32 whitespace-normal px-2.5',
                cell: (r) => (
                  <span className="grid gap-0.5" data-file-state={r.state}>
                    <Badge tone={STATE_TONE[r.state]}>{r.state === 'PENDING' && ['GENERATED', 'STORED', 'AWAITING_IDENTITY', 'AWAITING_CHECK'].includes(r.reasonCode) ? tr(`dossierPanel.fileState.${r.reasonCode}`) : tr(`dossierPanel.state.${r.state}`)}</Badge>
                    <span className="text-xs text-muted-foreground">{t('dossierPanel.files.reason', { reason: r.reason })}</span>
                  </span>
                ),
              },
              {
                key: 'vnnic',
                header: t('dossierPanel.files.vnnic'),
                className: 'px-2.5',
                cell: (r) => (r.sent ? <Badge tone="ok" icon={false}>{t('dossierPanel.files.sent')}</Badge> : <Badge tone="neutral" icon={false}>{t('dossierPanel.files.notSent')}</Badge>),
              },
              {
                key: 'dates',
                header: t('dossierPanel.files.dates'),
                className: 'whitespace-nowrap px-2.5',
                cell: (r) => (
                  <span className="grid text-xs leading-tight text-muted-foreground">
                    <span>{r.createdAt ? formatDate(r.createdAt) : '—'}</span>
                    <span>{r.updatedAt && r.updatedAt !== r.createdAt ? formatDate(r.updatedAt) : ''}</span>
                  </span>
                ),
              },
              {
                key: 'file',
                header: t('dossierPanel.files.file'),
                className: 'min-w-28 whitespace-normal px-2.5',
                cell: (r) => (
                  <FileCell
                    row={r}
                    signing={data.signing.status === 'success' ? data.signing.data : []}
                    batchTime={data.batchTime}
                    vn={data.vn}
                    onSign={() => setDialog({ kind: 'sign', row: r })}
                    onOpen={(download) => setViewing({ id: r.id, name: r.doc?.file_name ?? r.id, download })}
                  />
                ),
              },
              {
                key: 'actions',
                header: t('dossierPanel.files.actions'),
                className: 'whitespace-nowrap px-2.5 last:pr-5',
                cell: (r) => (
                  <span className="flex items-center gap-0.5">
                    {mayUpload && typeOf(r.purpose) ? <Button size="sm" variant="outline" onClick={() => onReplace(r)}><FileUp /> {t('dossierPanel.files.replace')}</Button> : null}
                  </span>
                ),
              },
            ]}
            rows={rows}
            rowKey={(r) => r.id}
            empty={<p className="text-sm text-muted-foreground">{t('dossierPanel.files.none')}</p>}
          />
        )}
        {olderRows.length ? (
          <details className="border-b px-5 py-3 text-sm" data-older-files>
            <summary className="cursor-pointer text-muted-foreground">
              <History className="mr-1 inline size-4" aria-hidden="true" />
              {t('dossierPanel.files.older', { count: olderRows.length })}
            </summary>
            <ul className="mt-2 grid gap-1.5">
              {olderRows.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{r.fileType !== null ? fileTypeName(r.fileType) : vocab('purpose', r.purpose)}</span>
                  <Badge tone={STATE_TONE[r.state]}>{tr(`dossierPanel.state.${r.state}`)}</Badge>
                  <span className="text-xs text-muted-foreground">{r.createdAt ? formatDateTime(r.createdAt) : '—'}</span>
                  <Button size="sm" variant="ghost" onClick={() => setViewing({ id: r.id, name: r.doc?.file_name ?? r.id, download: false })}>
                    <FileText /> {r.doc?.file_name ?? t('dossierPanel.files.view')}
                  </Button>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Block>

      {/* 3. The dossier on VNNIC. */}
      {data.vn ? (
        <VnnicBlock
          x={x}
          data={data}
          sync={sync}
          hstm={hstm}
          state={state}
          rows={rows}
          fileTypeName={fileTypeName}
          certifyName={certifyName}
          operation={operation}
          registrarSigner={rows.find((r) => r.declaration)?.bound?.registrar_signer ?? (x.registrar_attestations ?? []).find((a) => a.status === 'VALID')?.certificate_subject ?? null}
          onSync={refresh}
          domainKey={detail.domain.id}
        />
      ) : (
        <Block id="dossier-vnnic" title={t('dossierPanel.vnnic.title')}>
          <p className="text-[13px] text-muted-foreground">{t('dossierPanel.vnnic.notVn')}</p>
        </Block>
      )}

      {/* Dialogs. */}
      <ActionDialog open={dialog?.kind === 'upload'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={dialog?.kind === 'upload' ? t('dossierPanel.upload.dialogTitle', { kind: typeName(dialog.type) }) : ''}>
        {dialog?.kind === 'upload' ? (
          <UploadForm
            dossierId={x.id}
            type={dialog.type}
            individual={individual}
            file={dialog.file}
            onCancel={() => setDialog(null)}
            onDone={(pipeline) => {
              setDialog(null);
              if (fileInput.current) fileInput.current.value = '';
              toast.success(pipeline === 'DECLARATION' ? t('dossierPanel.upload.declarationDone') : t('dossierPanel.upload.done'));
              refresh();
            }}
          />
        ) : null}
      </ActionDialog>
      <ReadFileDialog
        open={dialog?.kind === 'template'}
        onOpenChange={(o) => (o ? undefined : setDialog(null))}
        load={(reason) => (template ? api.declarationPdf(template.declaration_id, reason) : Promise.reject(new Error('no declaration')))}
        onLoaded={(blob) => {
          saveBlob(blob, dossierFileName(detail.domain.ascii_name, DOCUMENT_CODE.REGISTRATION_FORM ?? 14, 'pdf'));
          toast.success(t('dossierPanel.upload.downloaded'));
        }}
      />
      <ActionDialog open={dialog?.kind === 'ekyc'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={t('dossierPanel.ekyc.title')}>
        {x.verification_copy && ekyc ? (
          <Dense
            rows={[
              [t('dossierPanel.ekyc.result'), <Badge key="r" tone={x.verification_copy.status === 'VERIFIED' ? 'ok' : 'warn'}>{vocab('check', x.verification_copy.status)}</Badge>],
              [t('dossierPanel.ekyc.score'), String(ekyc.summary.face_match_score ?? '—')],
              [t('dossierPanel.ekyc.requestId'), <code key="i" className="font-mono text-xs">{String(ekyc.summary.ekyc_request_id ?? '—')}</code>],
              [t('dossierPanel.ekyc.verifiedAt'), x.verification_copy.verified_at ? formatDateTime(x.verification_copy.verified_at) : null],
              [t('dossierPanel.ekyc.provider'), x.verification_copy.provider],
            ]}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{t('dossierPanel.ekyc.none')}</p>
        )}
      </ActionDialog>
      <ActionDialog open={dialog?.kind === 'identity'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={t('dossierPanel.identity.title')}>
        {dialog?.kind === 'identity' && x.subject_id ? <IdentityView subjectId={x.subject_id} signer={signer} ekyc={ekyc?.summary ?? null} /> : null}
      </ActionDialog>
      {review && mayReview ? (
        <>
          <ActionDialog open={dialog?.kind === 'approve' && canApprove(review)} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={reviewTitle} description={tq('panel.decisionHint')}>
            <DecisionForm item={review} dialog="approve" onDone={() => (setDialog(null), refresh())} onCancel={() => setDialog(null)} />
          </ActionDialog>
          <ActionDialog open={dialog?.kind === 'reject'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={tw('reject.title')} description={tw('reject.description')}>
            <DecisionForm item={review} dialog="reject" onDone={() => (setDialog(null), refresh())} onCancel={() => setDialog(null)} />
          </ActionDialog>
        </>
      ) : null}
      {queued ? (
        <ActionDialog open={dialog?.kind === 'remind'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={tq('remind.title')}>
          <RemindForm dossierId={x.id} domain={detail.domain.unicode_name} onDone={() => (setDialog(null), refresh())} onCancel={() => setDialog(null)} />
        </ActionDialog>
      ) : null}
      <ActionDialog open={dialog?.kind === 'sign'} onOpenChange={(o) => (o ? undefined : setDialog(null))} title={dialog?.kind === 'sign' ? t('dossierPanel.sign.title', { kind: dialog.row.fileType !== null ? fileTypeName(dialog.row.fileType) : vocab('purpose', dialog.row.purpose) }) : ''}>
        {dialog?.kind === 'sign' ? (
          <SignForm
            domainKey={detail.domain.id}
            row={dialog.row}
            item={signItemFor(dialog.row, data.signing.status === 'success' ? data.signing.data : [])}
            batchTime={data.batchTime}
            onCancel={() => setDialog(null)}
            onDone={() => (setDialog(null), refresh())}
          />
        ) : null}
      </ActionDialog>
      <FileViewer
        target={viewing}
        onClose={() => setViewing(null)}
        title={t('dossierPanel.files.readTitle', { name: viewing?.name ?? '' })}
        description={t('dossierPanel.files.readBody')}
        hint={t('dossierPanel.files.previewHint')}
        downloadLabel={t('dossierPanel.files.download')}
        marker="dossier-file-preview"
        load={(reason, f) => api.getDomainDocumentContent(detail.domain.id, viewing?.id ?? '', { reason, download: f.download })}
      />
      <Sheet open={history} onOpenChange={setHistory}>
        <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{t('dossierPanel.versions.title')}</SheetTitle>
            <SheetDescription>{detail.domain.unicode_name}</SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">{history ? <Versions x={x} /> : null}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CheckIcon({ state, label }: { state: CheckState; label: string }) {
  if (state === 'ok') return <Check className="size-4 shrink-0 text-success" aria-label={label} />;
  if (state === 'waiting') return <Clock className="size-4 shrink-0 text-warning" aria-label={label} />;
  if (state === 'missing') return <X className="size-4 shrink-0 text-critical" aria-label={label} />;
  return <CircleAlert className="size-4 shrink-0 text-muted-foreground" aria-label={label} />;
}

/** The registrar-signature queue item of a file: per file when the server says which, else the domain declaration's. */
function signItemFor(row: Row, items: readonly SignatureQueueItem[]): SignatureQueueItem | null {
  const own = items.filter((i) => i.status !== 'CANCELLED');
  const byFile = own.find((i) => (i as { document_version_id?: string | null }).document_version_id === row.id);
  if (byFile) return byFile;
  return row.declaration ? (own.find((i) => !(i as { document_version_id?: string | null }).document_version_id) ?? null) : null;
}

/** "Tệp": the link to the file, the registrar's shield, and the SmartCA signature right beside it. */
function FileCell({ row, signing, batchTime, vn, onSign, onOpen }: { row: Row; signing: readonly SignatureQueueItem[]; batchTime: string; vn: boolean; onSign: () => void; onOpen: (download: boolean) => void }) {
  const { t } = useTranslation('domainPage');
  const rs = row.doc?.registrar_signature ?? null;
  const signed = Boolean(rs?.signed) || Boolean(row.bound?.signed_by_registrar);
  // The file's own request; else the registrar-signature queue item that names it (or the declaration's).
  const legacy = rs?.request ? null : signItemFor(row, signing);
  const status = rs?.request?.status ?? legacy?.status ?? null;
  const lastError = rs?.request ? rs.request.last_error : legacy && legacy.attempts > 0 ? legacy.last_error : null;
  const pdf = (row.mime ?? '').includes('pdf');
  const expired = status === 'QUEUED' && Boolean(lastError);
  const inline = !status
    ? null
    : expired
      ? t('dossierPanel.sign.expired')
      : status === 'QUEUED'
        ? t('dossierPanel.sign.queued', { time: batchTime })
        : status === 'IN_BATCH'
          ? t('dossierPanel.sign.inBatch')
          : status === 'SIGNED'
            ? t('dossierPanel.sign.signed')
            : status === 'PENDING_CHECK'
              ? t('dossierPanel.sign.checking')
              : status === 'REVIEW'
                ? t('dossierPanel.sign.review')
                : null;
  const canSend = Boolean(rs?.can_request) || status === 'QUEUED';
  const why = canSend ? null : status ? inline : t('dossierPanel.sign.unavailable');
  return (
    <span className="grid gap-1">
      <span className="flex flex-wrap items-center gap-1.5">
        {row.doc ? (
          <span className="inline-flex min-w-0 items-center gap-0.5">
            <button
              type="button"
              className="inline-flex min-w-0 items-center gap-1 text-left text-[13px] text-primary hover:underline"
              title={row.doc.file_name ? `${t('dossierPanel.files.view')}: ${row.doc.file_name}` : t('dossierPanel.files.view')}
              data-file-name={row.doc.file_name ?? undefined}
              data-dossier-file-view
              onClick={() => onOpen(false)}
            >
              <FileText className="size-3.5 shrink-0" aria-hidden="true" /> <span className="break-all">{row.doc.file_name ?? t('dossierPanel.files.view')}</span>
            </button>
            <Button variant="ghost" size="icon" className="size-6" aria-label={t('dossierPanel.files.download')} title={t('dossierPanel.files.download')} onClick={() => onOpen(true)} data-dossier-file-download>
              <Download className="size-3.5" />
            </Button>
            {row.doc.document_id ? (
              <Link to={`/documents/${encodeURIComponent(row.doc.document_id)}`} className="inline-flex size-6 items-center justify-center rounded text-muted-foreground hover:text-foreground" aria-label={t('dossierPanel.files.details')} title={t('dossierPanel.files.details')}>
                <ExternalLink className="size-3.5" />
              </Link>
            ) : null}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{formatSize(row.size)}</span>
        )}
        {signed ? (
          <span
            className="inline-flex items-center gap-0.5 rounded bg-success-soft px-1.5 py-0.5 text-[11px] font-semibold text-success"
            title={[rs?.signer ?? row.bound?.registrar_signer, rs?.signed_at ? formatDateTime(rs.signed_at) : row.bound?.registrar_signed_date ? formatDate(row.bound.registrar_signed_date) : null].filter(Boolean).join(' · ')}
          >
            <ShieldCheck className="size-3" aria-hidden="true" /> {t('dossierPanel.files.registrarSigned')}
          </span>
        ) : vn && (pdf || rs?.can_request) ? (
          <Why why={why}>
            <Button variant="outline" size="sm" className="h-6 gap-1 px-1.5 text-[11px]" disabled={!canSend} onClick={onSign} data-sign-file>
              <PenLine className="size-3" /> {t('dossierPanel.sign.button')}
            </Button>
          </Why>
        ) : null}
      </span>
      {inline && !signed ? <span className={cn('text-[11px]', expired ? 'text-warning' : 'text-muted-foreground')}>{inline}</span> : null}
    </span>
  );
}

/**
 * "Ký SmartCA": "Vào lô" (the default) puts the file in the registrar-signature queue for the daily
 * batch; "Gửi ký ngay" also sends the waiting files to the SmartCA app now.
 */
function SignForm({ domainKey, row, item, batchTime, onDone, onCancel }: { domainKey: string; row: Row; item: SignatureQueueItem | null; batchTime: string; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('domainPage');
  const { t: tc } = useTranslation();
  const rs = row.doc?.registrar_signature ?? null;
  const status = rs?.request?.status ?? item?.status ?? null;
  const queued = status === 'QUEUED';
  const service = useSigningService();
  // One request: the server checks the file at once and, for "Gửi ký ngay", collects it into a
  // batch; a file already queued (the declaration signed automatically) goes with "Gửi ký ngay".
  const write = useWrite(async (mode: 'NOW' | 'BATCH', opts) => {
    if (rs?.can_request) return { accepted: Boolean((await api.requestRegistrarSignature(domainKey, row.id, { now: mode === 'NOW' }, opts)).accepted_declaration) };
    // Only this file's queue item, never the rest of the queue (owner 2026-10-06).
    if (mode === 'NOW' && item) await api.signQueuedNow({ item_id: item.id }, opts);
    return { accepted: false };
  });
  const send = async (mode: 'NOW' | 'BATCH') => {
    if (mode === 'BATCH' && !rs?.can_request) {
      toast.info(t('dossierPanel.sign.alreadyQueued', { time: batchTime }));
      onDone();
      return;
    }
    const done = await write.run(mode);
    if (done !== undefined) {
      toast.success(mode === 'NOW' ? t('dossierPanel.sign.sent') : t('dossierPanel.sign.queuedDone', { time: batchTime }), done.accepted ? { description: t('dossierPanel.sign.acceptedWhenSigning') } : undefined);
      onDone();
    }
  };
  return (
    <div className="grid gap-4">
      <p className="text-sm text-muted-foreground">{t('dossierPanel.sign.body', { time: batchTime })}</p>
      {queued ? <p className="text-sm">{t('dossierPanel.sign.alreadyQueued', { time: batchTime })}</p> : null}
      <SigningServiceNotice service={service.status === 'success' ? service.data : null} />
      {write.state.phase === 'error' ? <SigningError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="button" variant="outline" onClick={() => send('NOW')} disabled={write.busy}>
          <PenLine /> {t('dossierPanel.sign.now')}
        </Button>
        <Button type="button" onClick={() => send('BATCH')} disabled={write.busy}>
          <Clock /> {t('dossierPanel.sign.batch', { time: batchTime })}
        </Button>
      </FormActions>
    </div>
  );
}

function UploadForm({ dossierId, type, file, individual, onDone, onCancel }: { dossierId: string; type: DossierDocumentType; file: File; individual: boolean; onDone: (pipeline?: 'DECLARATION' | 'DOSSIER_FILE') => void; onCancel: () => void }) {
  const { t } = useTranslation('domainPage');
  const { t: tc } = useTranslation();
  const [reason, setReason] = useState('');
  // The type's rules and VNNIC's file rule for it, checked again by the server: extension and size.
  const rule = uploadProblem(type, file);
  const problem = !rule
    ? null
    : rule.code === 'extension'
      ? t('dossierPanel.upload.badExtension', { list: rule.list.join(', ') })
      : rule.code === 'tooLarge'
        ? t('dossierPanel.upload.tooLarge', { max: formatSize(rule.max) })
        : t('dossierPanel.upload.tooSmall', { min: formatSize(rule.min) });
  // #14 the domain declaration: the same pipeline as the customer's upload (checks, staff decision, registrar signature)
  // for an organisation. An individual is verified only by eKYC (owner decision 2026-10-01, ADR-024 decision log 17):
  // the declaration uploaded for one is only stored as a dossier file, it never replaces the eKYC.
  const declaration = type.upload_purpose === null && type.hstm_file_type === 5;
  const write = useWrite(async (_: null, opts) => api.uploadDossierFile(dossierId, { document_type: type.id, mime_type: file.type, file_name: file.name, data: await readBase64(file), reason: reason.trim() }, opts));
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!problem && reason.trim()) {
          const done = await write.run(null);
          if (done) onDone(done.pipeline);
        }
      }}
    >
      <p className="text-sm text-muted-foreground">
        {declaration && !individual ? t('dossierPanel.upload.declarationBody', { file: file.name }) : t('dossierPanel.upload.dialogBody', { file: file.name })}
      </p>
      {declaration && individual ? (
        <Callout tone="warn" title={t('dossierPanel.upload.individualDeclarationTitle')}>
          <p>{t('dossierPanel.upload.individualDeclaration')}</p>
        </Callout>
      ) : null}
      {problem ? <p className="text-sm text-critical">{problem}</p> : null}
      <Field label={t('dossierPanel.upload.reason')}>
        <Textarea rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} />
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !reason.trim() || problem !== null}>
          <Upload /> {t('dossierPanel.upload.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

/** "Xem thông tin CCCD": the registrant's details, masked, with the eKYC result and the declaration's signer. */
function IdentityView({ subjectId, signer, ekyc }: { subjectId: string; signer: string | null; ekyc: Record<string, unknown> | null }) {
  const { t } = useTranslation('domainPage');
  const { t: td } = useTranslation('domains');
  const trd = td as unknown as Lookup;
  const r = useApi((signal) => api.getSubject(subjectId, { signal }), [subjectId]);
  if (r.status === 'loading') return <Loading rows={3} />;
  if (r.status === 'error') return <DataError error={r.error} onRetry={r.reload} title={t('dossierPanel.identity.failed')} />;
  const s = r.data;
  const p = s.profile ?? null;
  const v = s.versions?.find((x) => x.id === s.current_version_id) ?? s.versions?.[0] ?? null;
  const identifiers = p?.identifiers ?? v?.identifiers ?? [];
  return (
    <div className="grid gap-3">
      <Dense
        rows={[
          [t('dossierPanel.identity.name'), p?.legal_name ?? v?.legal_name ?? null],
          [
            t('dossierPanel.identity.ids'),
            identifiers.length ? (
              <span key="i" className="grid">
                {identifiers.map((i, k) => (
                  <span key={k}>
                    {trd(`subject.identifierTypes.${i.type}`, { defaultValue: i.type })}: <code className="font-mono text-xs">{i.hint ?? '••••'}</code>
                  </span>
                ))}
              </span>
            ) : null,
          ],
          s.subject_type === 'INDIVIDUAL' && p ? [t('dossierPanel.identity.birth'), p.has_birth_date ? t('dossierPanel.identity.birthOnFile') : null] : null,
          [t('dossierPanel.identity.nationality'), p?.nationality ?? v?.nationality ?? null],
          p?.vn_address || p?.address ? [t('dossierPanel.identity.address'), p.vn_address?.full_address ?? (p.address ? addressLine(p.address) : '')] : null,
          signer ? [t('dossierPanel.identity.signer'), <code key="s" className="font-mono text-xs">{signer}</code>] : null,
          ekyc ? [t('dossierPanel.ekyc.score'), String(ekyc.face_match_score ?? '—')] : null,
          ekyc ? [t('dossierPanel.ekyc.requestId'), <code key="e" className="font-mono text-xs">{String(ekyc.ekyc_request_id ?? '—')}</code>] : null,
        ]}
      />
      <p className="text-xs text-muted-foreground">{t('dossierPanel.identity.masked')}</p>
      <div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/subjects/${encodeURIComponent(subjectId)}`}>{t('dossierPanel.identity.openSubject')}</Link>
        </Button>
      </div>
    </div>
  );
}

/** 3. "Hồ sơ trên VNNIC": the HSTM view of the dossier, or one line of what is still missing. */
function VnnicBlock({
  x,
  data,
  sync,
  hstm,
  state,
  rows,
  fileTypeName,
  certifyName,
  operation,
  registrarSigner,
  onSync,
  domainKey,
}: {
  x: DomainDossier;
  data: ReturnType<typeof useDossierData>;
  sync: DomainVnnic['sync'];
  hstm: DomainHstmDossier | null;
  state: VnnicState | null;
  rows: Row[];
  fileTypeName: (n: number) => string;
  certifyName: (n: number | null, fallback?: string | null) => string;
  operation: string;
  registrarSigner: string | null;
  onSync: () => void;
  domainKey: string;
}) {
  const { t } = useTranslation('domainPage');
  const tr = t as unknown as Lookup;
  const files = x.vnnic_files ?? null;
  const [open, setOpen] = useState<(FileTarget & { id: string }) | null>(null);
  const blockers = vnnicBlockers(files);
  const blockerText = (b: Blocker): string => {
    if (b.kind === 'error') return tr(`dossierPanel.blocker.${b.code}`);
    if (b.kind === 'rejected') return t('dossierPanel.blocker.rejected', { name: fileTypeName(b.fileType), problem: tr(`dossierPanel.reason.${b.problem}`) });
    const name = b.alternatives.map((alt) => alt.map(fileTypeName).join(' + ')).join(t('dossierPanel.blocker.or'));
    return b.signedStatus === 2 ? t('dossierPanel.blocker.signed2', { name }) : b.signedStatus === 1 ? t('dossierPanel.blocker.signed1', { name }) : name;
  };
  const method = hstm?.verification?.method ?? files?.certify_method ?? x.hstm_certify_method ?? null;
  const ekycEvidence = x.verification_copy?.evidence.find((e) => e.evidence_type === 'EKYC_RESULT')?.summary ?? null;
  const ekycScore = hstm?.verification?.ekyc_score ?? (ekycEvidence?.face_match_score as number | string | undefined) ?? null;
  const ekycId = hstm?.verification?.ekyc_request_id ?? (ekycEvidence?.ekyc_request_id as string | undefined) ?? null;
  // Who signed: the registrant's signed file (#2 / #4), the registrar's declaration (#5).
  const subjectSigned = rows.find((r) => r.bound?.signed_by_subject)?.bound ?? null;
  const signedBy = hstm?.verification?.signed_by ?? (method === 5 ? registrarSigner : (subjectSigned?.signer ?? null));
  const signedAt = hstm?.verification?.signed_at ?? (method === 5 ? (rows.find((r) => r.declaration)?.bound?.registrar_signed_date ?? null) : (subjectSigned?.signed_date ?? null));
  const sentFiles =
    hstm?.files ??
    (files?.files ?? []).map((f) => ({
      // Not sent yet: the platform's own copy (the document version).
      id: f.document_version_id,
      name: `${f.stem}.${f.mime_type.includes('pdf') ? 'pdf' : f.mime_type.split('/')[1] === 'jpeg' ? 'jpg' : (f.mime_type.split('/')[1] ?? 'bin')}`,
      size: f.size_bytes,
      file_type: f.file_type,
      file_type_label: null,
      signed_status: f.signed_status,
      signed_by_label: null,
      created_at: rows.find((r) => r.id === f.document_version_id)?.createdAt ?? null,
    }));
  const reason = hstm?.reason ?? (state === 'REJECTED' ? (sync?.last_transaction?.error_message ?? null) : null);
  const sent = state !== null && state !== 'NOT_SENT';
  return (
    <Block
      id="dossier-vnnic"
      title={t('dossierPanel.vnnic.title')}
      actions={
        <Button size="sm" variant="outline" title={t('dossierPanel.vnnic.syncHint')} onClick={onSync} disabled={data.vnnic.status === 'loading'}>
          <RefreshCw className={cn(data.vnnic.status === 'loading' && 'animate-spin')} /> {t('dossierPanel.vnnic.sync')}
        </Button>
      }
    >
      {data.vnnic.status === 'loading' ? <Loading rows={2} /> : null}
      {data.vnnic.status === 'error' ? <DataError error={data.vnnic.error} onRetry={data.vnnic.reload} title={t('dossierPanel.vnnic.failed')} /> : null}
      {data.vnnic.status === 'success' && (!sent || (state !== 'APPROVED' && blockers.length > 0)) ? (
        <p className="flex items-start gap-2 text-[13px]" data-vnnic-not-sent>
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <span className="font-semibold">{sent ? t('dossierPanel.vnnic.resend') : t('dossierPanel.vnnic.notSent')}</span>
            {' — '}
            {blockers.length ? t('dossierPanel.vnnic.missing', { list: blockers.map(blockerText).join('; ') }) : x.status === 'COMPLETE' ? t('dossierPanel.vnnic.ready') : t('dossierPanel.vnnic.notReady')}
          </span>
        </p>
      ) : null}
      {data.vnnic.status === 'success' && sent && state ? (
        <div className="mt-3 grid gap-3 first:mt-0">
          <Dense
            rows={[
              [t('dossierPanel.vnnic.type'), hstm?.document_type ?? operation],
              [t('dossierPanel.vnnic.id'), <code key="id" className="font-mono text-xs">{String(hstm?.dossier_id ?? sync?.external_reference ?? '—')}</code>],
              hstm?.created_by ? [t('dossierPanel.vnnic.createdBy'), hstm.created_by] : null,
              [t('dossierPanel.vnnic.createdAt'), formatDateTime(hstm?.created_at ?? sync?.last_transaction?.submitted_at ?? null)],
              hstm?.updated_by ? [t('dossierPanel.vnnic.updatedBy'), hstm.updated_by] : null,
              [t('dossierPanel.vnnic.updatedAt'), formatDateTime(hstm?.updated_at ?? sync?.status_changed_at ?? null)],
              [t('dossierPanel.vnnic.method'), hstm?.verification?.method_label ? `#${method} ${hstm.verification.method_label}` : certifyName(method, files?.certify_method_name)],
              method === 1 || method === 3 ? [t('dossierPanel.vnnic.ekycScore'), ekycScore !== null ? String(ekycScore) : null] : null,
              method === 1 || method === 3 ? [t('dossierPanel.vnnic.ekycRequest'), ekycId ? <code key="e" className="font-mono text-xs">{ekycId}</code> : null] : null,
              method === 2 || method === 4 || method === 5 ? [t('dossierPanel.vnnic.signer'), signedBy] : null,
              method === 2 || method === 4 || method === 5 ? [t('dossierPanel.vnnic.signedAt'), signedAt ? formatDate(signedAt) : null] : null,
              [
                t('dossierPanel.vnnic.status'),
                <span key="s" className="flex flex-wrap items-center gap-1.5">
                  <Badge tone={VNNIC_TONE[state]}>{tr(`dossierPanel.vnnic.statusValue.${state}`)}</Badge>
                  {reason ? <span className="text-xs text-muted-foreground">{t('dossierPanel.files.reason', { reason })}</span> : null}
                </span>,
              ],
            ]}
          />
          {sentFiles.length ? (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-[12.5px]" data-vnnic-files>
                <thead className="bg-muted/50 text-[11px] text-muted-foreground uppercase">
                  <tr>
                    {(['fileId', 'fileName', 'fileSize', 'fileType', 'fileSign', 'fileCreated'] as const).map((k) => (
                      <th key={k} className="px-3 py-1.5 text-left font-semibold">
                        {t(`dossierPanel.vnnic.${k}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sentFiles.map((f) => (
                    <tr key={String(f.id)} className="border-t">
                      <td className="px-3 py-1.5 font-mono text-xs">
                        <button type="button" className="text-primary hover:underline" onClick={() => setOpen({ id: String(f.id), name: f.name, download: false })} title={t('dossierPanel.vnnic.fileView')}>
                          {String(f.id).length > 12 ? `${String(f.id).slice(0, 8)}…` : String(f.id)}
                        </button>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="flex items-center gap-1">
                          <button type="button" className="text-left text-primary hover:underline" onClick={() => setOpen({ id: String(f.id), name: f.name, download: false })} data-vnnic-file-view>
                            {f.name}
                          </button>
                          <Button variant="ghost" size="icon" className="size-6" aria-label={t('dossierPanel.vnnic.fileDownload')} title={t('dossierPanel.vnnic.fileDownload')} onClick={() => setOpen({ id: String(f.id), name: f.name, download: true })} data-vnnic-file-download>
                            <Download className="size-3.5" />
                          </Button>
                        </span>
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{formatSize(f.size)}</td>
                      <td className="px-3 py-1.5">{f.file_type_label ?? fileTypeName(f.file_type)}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{f.signed_by_label ?? tr(`dossierPanel.vnnic.signedBy.${f.signed_status}`, { defaultValue: '—' })}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{f.created_at ? formatDate(f.created_at) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
      <FileViewer
        target={open}
        onClose={() => setOpen(null)}
        title={t('dossierPanel.vnnic.fileReadTitle', { name: open?.name ?? '' })}
        description={t('dossierPanel.vnnic.fileReadBody')}
        hint={t('dossierPanel.vnnic.filePreviewHint')}
        downloadLabel={t('dossierPanel.vnnic.fileDownload')}
        marker="vnnic-file-preview"
        load={async (why, f) => {
          const id = open?.id;
          if (!id) throw new Error(t('dossierPanel.vnnic.fileUnavailable'));
          try {
            return await api.getVnnicFile(domainKey, id, { reason: why, download: f.download });
          } catch (e) {
            if (isApiError(e) && (e.code.startsWith('VNNIC_FILE_') || e.status >= 500)) throw new Error(t('dossierPanel.vnnic.fileUnavailable'), { cause: e });
            throw e;
          }
        }}
      />
    </Block>
  );
}

/** "Lịch sử" drawer: every version of the dossier and the status changes of this one. */
function Versions({ x }: { x: DomainDossier }) {
  const { t } = useTranslation('domainPage');
  const r = useApi((signal) => api.getDossier(x.id, { signal }), [x.id]);
  if (r.status === 'loading') return <Loading rows={2} />;
  if (r.status === 'error') return <DataError error={r.error} onRetry={r.reload} title={t('dossierPanel.versions.failed')} />;
  return (
    <div className="grid gap-5">
      <ul className="grid gap-2">
        {[...r.data.versions].sort((a, b) => b.dossier_version - a.dossier_version).map((v) => (
          <li key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm">
            <span className="font-semibold">v{v.dossier_version}</span>
            <StatusBadge map={DOSSIER} value={v.status} />
            <span className="text-xs text-muted-foreground">{t('dossierPanel.versions.created', { date: formatDateTime(v.created_at) })}</span>
            {v.completed_at ? <span className="text-xs text-muted-foreground">{t('dossierPanel.versions.completed', { date: formatDateTime(v.completed_at) })}</span> : null}
            {v.id === x.id ? <Badge tone="info" icon={false}>{t('dossierPanel.versions.current')}</Badge> : null}
          </li>
        ))}
      </ul>
      {r.data.status_history.length ? (
        <ol className="relative space-y-3 border-l pl-4">
          {[...r.data.status_history].reverse().map((h, k) => (
            <li key={k} className="relative text-sm">
              <span aria-hidden="true" className="absolute top-1.5 -left-[21px] size-2.5 rounded-full border-2 border-card bg-primary" />
              <div className="font-medium">
                {h.from_status ? (
                  <>
                    <StatusBadge map={DOSSIER} value={h.from_status} /> →{' '}
                  </>
                ) : null}
                <StatusBadge map={DOSSIER} value={h.to_status} />
              </div>
              <div className="text-xs text-muted-foreground">
                {formatDateTime(h.occurred_at)}
                {h.actor_name ? ` · ${h.actor_name}` : ''}
                {h.reason ? (
                  <>
                    {' · '}
                    <SystemReason reason={h.reason} />
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
